/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client';

import { type JSX } from 'react';
import { useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

export type SubPaginationProps = {
    /** Page size (items per page). */
    limit: number;
    /** Current 0-based offset of the active page in the result set. */
    offset: number;
    /** Total number of items across all pages. */
    total: number;
    /** Optional className applied to the wrapper. */
    className?: string;
};

/**
 * SubPagination
 *
 * Compact text indicator showing the current item range and total result count,
 * designed to sit beside the sort control in the PLP toolbar (Figma node
 * 12165:93209).
 *
 * Responsive behavior:
 *   - Below `sm` (mobile)         → "{{total}} results"
 *   - At `sm` and above (desktop) → "{{start}} - {{end}} of {{total}} results"
 *
 * The mobile copy mirrors the Figma mobile toolbar where the full range would
 * crowd out the filter button and sort control. We render both variants and
 * toggle visibility via Tailwind's `sm:` breakpoint so the active text matches
 * the viewport at first paint (no JS-driven hydration mismatch).
 *
 * Derives the visible range from the same `limit` / `offset` / `total` inputs
 * used by the {@link Pagination} control. While a navigation is in-flight, the
 * optimistic offset is read from the in-flight destination URL.
 *
 * Returns `null` when there are no results to describe.
 */
export default function SubPagination({ limit, offset, total, className }: SubPaginationProps): JSX.Element | null {
    const navigation = useNavigation();
    const { t } = useTranslation('category');

    /**
     * Optimistic offset derived from the in-flight navigation target so the
     * displayed range matches the destination page immediately on click, before
     * the loader settles. Once it settles we fall back to the server-provided
     * offset.
     */
    const effectiveOffset = navigation.location
        ? Number(
              new URLSearchParams(navigation.location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET) || String(offset)
          )
        : offset;

    if (total <= 0) {
        return null;
    }

    const safeLimit = Math.max(1, limit);
    const safeOffset = Math.min(Math.max(0, effectiveOffset), Math.max(0, total - 1));
    const start = safeOffset + 1;
    const end = Math.min(total, safeOffset + safeLimit);

    const totalOnlyText = t('subPagination.totalOnly', {
        total,
        defaultValue: '{{total}} results',
    });
    const fullRangeText = t('subPagination.results', {
        start,
        end,
        total,
        defaultValue: '{{start}} - {{end}} of {{total}} results',
    });

    return (
        <p
            data-slot="sub-pagination"
            data-testid="sub-pagination"
            className={cn('text-sm leading-5 text-foreground whitespace-nowrap', className)}>
            {/* Mobile: only the total count. Hidden on `md+`. */}
            <span data-testid="sub-pagination-mobile" className="md:hidden">
                {totalOnlyText}
            </span>
            {/* Desktop: full "start - end of total". Hidden below `md`. */}
            <span data-testid="sub-pagination-desktop" className="hidden md:inline">
                {fullRangeText}
            </span>
        </p>
    );
}
